<template>
  <div>
    <RuleBuilderGroup
      :group="group"
      :config="approvalConfig"
      :depth="depth"
      operator-label="Tipo de validação"
      @update:group="(updated) => handleUpdate(updated as ApprovalGroup)"
      @remove="emit('remove')"
    >
      <template #rule="{ rule, update, removable }">
        <div class="flex items-center gap-2">
          <ApprovalRoleSelector
            :model-value="(rule as ApprovalRule).value"
            :disabled="!removable"
            :error="showErrors && (rule as ApprovalRule).value === null"
            @update:model-value="update"
          />
          <div
            v-if="!removable"
            class="flex items-center gap-1.5 px-2 py-1 border border-primary30 rounded bg-primary5 whitespace-nowrap"
            data-testid="badge-perfil-padrao"
          >
            <v-icon :icon="mdiFormTextboxLock" size="16" class="text-primary30" />
            <span class="text-sm text-secondary50">Perfil Padrão</span>
          </div>
        </div>
      </template>
    </RuleBuilderGroup>

    <ConfirmDeleteRoleModal
      :model-value="showDeleteModal"
      :title="deleteTitle"
      :description="deleteDescription"
      :informational="deleteType === 'mandatory-rule'"
      @confirm="handleConfirm"
      @cancel="handleCancel"
      @update:model-value="
        (v) => {
          if (!v) handleCancel()
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { mdiFormTextboxLock } from '@mdi/js'

import { RuleBuilderGroup } from '@/components/designSystem/RuleBuilder'
import type { RuleBuilderConfig } from '@/components/designSystem/RuleBuilder'
import ApprovalRoleSelector from './ApprovalRoleSelector.vue'
import ConfirmDeleteRoleModal from '../modals/ConfirmDeleteRoleModal.vue'
import { ensureMandatoryRoles } from '@/utils/approval-flow-transformers'

import {
  LayerOperator,
  MANDATORY_VALIDATION_ROLES,
  ROOT_LAYER_REQUIRES_AND,
  VALIDATION_ROLE_LABELS,
  ValidationRole
} from '@/types/approval-flow.types'
import type { ApprovalGroup, ApprovalRule } from '@/types/approval-flow.types'
import type { RuleBuilderChild } from '@/components/designSystem/RuleBuilder'

interface Props {
  group: ApprovalGroup
  depth?: number
  showErrors?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
  showErrors: false
})

const emit = defineEmits<{
  (e: 'update:group', group: ApprovalGroup): void
  (e: 'remove'): void
}>()

type DeleteType = 'rule' | 'group' | 'mandatory-rule'

/**
 * Reordenar por drag é a única via que ainda conseguiria tirar os obrigatórios
 * do topo — eles não têm botão de remover e o seletor está desabilitado. Como o
 * peso enviado à API vem da posição, um obrigatório arrastado para o meio
 * reapareceria fora de ordem no reload. Renormalizar na emissão faz a lista
 * voltar sozinha, coerente com o rótulo "Perfil Padrão".
 *
 * Só a raiz é normalizada; `ensureMandatoryRoles` é idempotente, então mudanças
 * vindas de subgrupos passam intactas.
 */
function handleUpdate(updated: ApprovalGroup) {
  emit('update:group', props.depth === 0 ? ensureMandatoryRoles(updated) : updated)
}

const showDeleteModal = ref(false)
const deleteType = ref<DeleteType>('rule')
// Papel que o usuário tentou remover, para o modal citá-lo pelo nome.
const blockedRole = ref<ValidationRole | null>(null)
let resolvePromise: ((value: boolean) => void) | null = null

const deleteTitle = computed(() => {
  if (deleteType.value === 'mandatory-rule') return 'Perfil obrigatório'
  return deleteType.value === 'rule' ? 'Excluir regra' : 'Excluir grupo'
})

const deleteDescription = computed(() => {
  if (deleteType.value === 'mandatory-rule') {
    const label = blockedRole.value ? VALIDATION_ROLE_LABELS[blockedRole.value] : 'obrigatório'
    return `O papel "${label}" é obrigatório em todos os fluxos de aprovação e não pode ser removido. Este perfil garante o cumprimento das normas de compliance e auditoria interna.`
  }
  return deleteType.value === 'rule'
    ? 'Tem certeza que deseja excluir essa regra de validação de excepcionalidade? Essa ação não poderá ser revertida'
    : 'Tem certeza que deseja excluir esse grupo de regras de validação de excepcionalidade? Essa ação não poderá ser revertida'
})

function requestConfirmation(type: DeleteType): Promise<boolean> {
  deleteType.value = type
  showDeleteModal.value = true
  return new Promise<boolean>((resolve) => {
    resolvePromise = resolve
  })
}

function handleConfirm() {
  showDeleteModal.value = false
  resolvePromise?.(true)
  resolvePromise = null
}

function handleCancel() {
  showDeleteModal.value = false
  resolvePromise?.(false)
  resolvePromise = null
}

/**
 * Protege a PRIMEIRA ocorrência de cada papel obrigatório no nível raiz.
 *
 * A comparação final é por identidade, não por valor: uma segunda regra com o
 * mesmo papel, ou uma ocorrência dentro de subgrupo, continua removível — só o
 * exemplar que sustenta a regra é intocável.
 */
function mandatoryRoleOf(child: RuleBuilderChild<string, unknown>): ValidationRole | null {
  if (child.type !== 'rule') return null

  const role = MANDATORY_VALIDATION_ROLES.find((mandatory) => child.value === mandatory)
  if (!role) return null

  const firstIndex = props.group.children.findIndex((c) => c.type === 'rule' && c.value === role)
  return firstIndex !== -1 && child === props.group.children[firstIndex] ? role : null
}

function isMandatoryRole(child: RuleBuilderChild<string, unknown>): boolean {
  return mandatoryRoleOf(child) !== null
}

const approvalConfig: RuleBuilderConfig<LayerOperator> = {
  operators: [
    {
      value: LayerOperator.AND,
      label: 'Todos - E',
      description: 'É necessária a aprovação de todos os validadores do grupo'
    },
    {
      value: LayerOperator.OR,
      label: 'Qualquer um - OU',
      description: 'É necessária a aprovação de apenas um membro do grupo'
    }
  ],
  maxDepth: 3,
  ruleLabel: (n) => `Validação ${n}`,
  addRuleLabel: 'Nova regra',
  addGroupLabel: 'Novo grupo',
  defaultOperator: LayerOperator.AND,
  isRuleRemovable: (child) => {
    return !isMandatoryRole(child)
  },
  beforeRemoveRule: (child) => {
    const mandatory = child ? mandatoryRoleOf(child) : null
    if (mandatory) {
      blockedRole.value = mandatory
      return requestConfirmation('mandatory-rule')
    }
    return requestConfirmation('rule')
  },
  beforeRemoveGroup: () => requestConfirmation('group'),
  // Com a flag ligada, a raiz combina os papéis obrigatórios em "E". Sob "OU"
  // bastaria a assinatura de um deles e a exigência conjunta deixaria de valer,
  // então o operador dela não é editável nesse regime — os subgrupos seguem
  // sempre livres. Com a flag desligada, a raiz também fica livre.
  isOperatorLocked: (depth) => ROOT_LAYER_REQUIRES_AND && depth === 0
}
</script>
